import axios from "axios";
import {BRAND_NAME} from "../../../../config/_variable";
import getConfigByName from "../../../../config";
import {verificationAxios as verificationAxiosInstance} from "../verification";
import {verificationAxios as questionnaireAxiosInstance} from "../questionnaire";
import {getFingerprintMetadata} from "../../../../config/fingerprintConfig";

const projectName = "web_tradersroom";
const LOGGING_ENABLED = true;
//const LOGGING_ENABLED = BRAND_NAME !== "staging";

const config = getConfigByName();
const brandName = config?.assetsBrandName || BRAND_NAME;
const logsURL = `${config?.backendURL}api/ms/logs`;

const SENSITIVE_FIELDS = new Set([
    "password", "password_confirm", "confirmPassword",
    "currentPassword", "newPassword", "existingPassword", "pin", "secret", "token",
]);

function redact(data, depth = 0) {
    if (depth > 5 || data === null || data === undefined) return data;
    if (Array.isArray(data)) return data.map((v) => redact(v, depth + 1));
    if (typeof data === "object" && !(data instanceof FormData) && !(data instanceof Error)) {
        const result = {};
        for (const [key, value] of Object.entries(data)) {
            result[key] = SENSITIVE_FIELDS.has(key) ? "[REDACTED]" : redact(value, depth + 1);
        }
        return result;
    }
    return data;
}

const processError = (e) => {
    if (axios.isAxiosError(e)) {
        return {
            type: "AxiosError",
            message: e.message,
            code: e.code,
            url: e.config?.url,
            method: e.config?.method,
            status: e.response?.status,
            response: e.response?.data,
        };
    }
    if (e instanceof Error) {
        return {type: "Error", message: e.message, stack: e.stack};
    }
    return {type: typeof e, value: e};
};

export const createLog = async (data = {}) => {
    if (!LOGGING_ENABLED) return;
    const logId = data?.logId || `${projectName}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const sequence = data?.sequence || 1;
    let logData = data?.logData;

    if (logData instanceof Error || axios.isAxiosError(logData)) {
        logData = processError(logData);
    } else {
        if (logData?.error && typeof logData.error === "object") {
            const {error, ...rest} = logData;
            if (error instanceof Error || axios.isAxiosError(error)) {
                logData = {...rest, error: processError(error)};
            } else {
                logData = {...error, ...rest};
            }
        }
        logData = redact(logData);
    }

    // Collected fresh on every call (not cached at page load) — network/VPN state can change
    // mid-session, and browserIp/geo are exactly the signals used to catch that. Merged in
    // first so a caller-supplied field always wins, and dropped entirely on failure rather
    // than leaking {error: ...} onto a normal log record.
    const fingerprintMetadata = await getFingerprintMetadata().catch(() => null);

    const accessToken = localStorage.getItem("a_t");

    await axios
        .post(`${logsURL}/logs/${projectName}/create`, {
            ...(fingerprintMetadata?.error ? null : fingerprintMetadata),
            status: data?.status || "info",
            module: data?.module || "System",
            submodule: data?.submodule || "General",
            function: data?.function || "unknown",
            description: data?.description || "",
            logId,
            sequence,
            createdBy: data?.createdBy ?? (localStorage.getItem("username") || localStorage.getItem("userid") || null),
            involveUserId: Array.isArray(data.involveUserId)
                ? data.involveUserId.join(",")
                : data.involveUserId?.toString() || null,
            tradingAccountId: Array.isArray(data.tradingAccountId)
                ? data.tradingAccountId.join(",")
                : data.tradingAccountId?.toString() || null,
            tradingAccountNo: Array.isArray(data.tradingAccountNo)
                ? data.tradingAccountNo.join(",")
                : data.tradingAccountNo?.toString() || null,
            logData: logData ?? {},
            brand: brandName,
            // Omitted (undefined) rather than sent as false/null — the backend already defaults
            // an absent value to "system", so this only ever goes over the wire as "user".
            triggerBy: data?.triggerBy === "user" ? "user" : undefined,
        }, accessToken ? {headers: {authorization: `Bearer ${accessToken}`}} : undefined)
        .catch((e) => {
            console.error("failed to create log:", e?.response?.data?.errObj || e);
        });
};

class LogSession {
    logId;
    sequence = 0;
    module;
    submodule;
    func;
    involveUserId;
    // Whole-session, not per-step — one logId is one user action (or one system action),
    // so every step within it shares the same trigger, decided once up front rather than
    // re-declared on each individual create() call. Defaults to true since almost every
    // session is created from a user-initiated handler (click/submit/confirm) — pass false
    // explicitly for sessions started from a poll/refresh/auto-save/system path.
    triggerByUser;

    constructor(module, submodule, func, triggerByUser = true) {
        this.logId = `${projectName}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        this.module = module;
        this.submodule = submodule;
        this.func = func;
        this.triggerByUser = triggerByUser;
    }

    getLogId() {
        return this.logId;
    }

    nextSequence() {
        this.sequence += 1;
        return this.sequence;
    }

    setInvolveUser(userId) {
        this.involveUserId = userId || null;
        return this;
    }

    // Defaults to true — call it only from a handler that fired because the user did
    // something (a click, a submit), never from a poll/refresh/auto-save path. Leaving
    // it unset keeps the backend's own default of "system".
    setTriggerByUser(value = true) {
        this.triggerByUser = value;
        return this;
    }

    create(input) {
        createLog({
            ...input,
            logId: this.logId,
            sequence: this.nextSequence(),
            involveUserId: input.involveUserId ?? this.involveUserId,
            module: input.module || this.module,
            submodule: input.submodule || this.submodule,
            function: input.function || this.func,
            triggerBy: this.triggerByUser ? "user" : undefined,
        });
    }
}

export function createLogSession(module, submodule, func, triggerByUser = true) {
    return new LogSession(module, submodule, func, triggerByUser);
}

/* ------------------------------------------------------------------ */
/*  Global error interceptors                                          */

/* ------------------------------------------------------------------ */

export function initGlobalLogging() {
    if (!LOGGING_ENABLED) return;
    window.onerror = (msg, source, line, col, error) => {
        createLog({
            status: "error",
            module: "System",
            submodule: "Runtime",
            function: "onerror",
            description: msg,
            logData: {source, line, col, error: error ? processError(error) : null},
        });
    };

    window.addEventListener("unhandledrejection", (e) => {
        createLog({
            status: "error",
            module: "System",
            submodule: "Runtime",
            function: "unhandledRejection",
            description: e.reason?.message || "Unhandled Promise Rejection",
            logData: processError(e.reason),
        });
    });

    const addErrorInterceptor = (instance) => {
        instance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status >= 500) {
                    createLog({
                        status: "error",
                        module: "System",
                        submodule: "Axios",
                        function: "responseError",
                        description: `${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status}`,
                        logData: {
                            status: error.response?.status,
                            url: error.config?.url,
                            method: error.config?.method,
                            data: error.response?.data,
                        },
                    });
                }
                return Promise.reject(error);
            }
        );
    };

    addErrorInterceptor(axios);
    addErrorInterceptor(verificationAxiosInstance);
    addErrorInterceptor(questionnaireAxiosInstance);

    const orgCreate = axios.create.bind(axios);
    axios.create = function (...args) {
        const instance = orgCreate(...args);
        addErrorInterceptor(instance);
        return instance;
    };
}
